import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api } from './api';
import {
  PROACTIVE_STATUS_LABELS,
  PROACTIVE_STATUS_TONE,
  ROLE_LABELS,
  STATUS_LABELS,
  STATUS_TONE,
} from './shared/constants';
import { formatTaipeiDate } from './shared/domain';
import type { ProactiveProposalStatus, QuotationStatus, Role, User } from './shared/types';

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-3 text-ink no-underline" aria-label="回到儀表板">
      <span className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-600 text-lg font-extrabold text-white shadow-soft">
        P
      </span>
      {!compact && (
        <span>
          <span className="block text-sm font-extrabold tracking-wide">PHARMAFLOW</span>
          <span className="block text-xs text-slate-500">惠生合作供應商平台</span>
        </span>
      )}
    </Link>
  );
}

export function StatusBadge({ status }: { status: QuotationStatus | ProactiveProposalStatus }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${
        (PROACTIVE_STATUS_TONE as Record<string, string>)[status] ??
        (STATUS_TONE as Record<string, string>)[status] ??
        'bg-slate-100 text-slate-700'
      }`}
    >
      {(PROACTIVE_STATUS_LABELS as Record<string, string>)[status] ??
        (STATUS_LABELS as Record<string, string>)[status] ??
        status}
    </span>
  );
}

export function RoleBadge({ role }: { role: Role }) {
  return (
    <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">
      {ROLE_LABELS[role]}
    </span>
  );
}

export function Button({
  className = '',
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}) {
  const variants = {
    primary: 'bg-teal-600 text-white shadow-sm hover:bg-teal-700',
    secondary: 'border border-slate-200 bg-white text-ink hover:border-teal-300 hover:bg-teal-50',
    ghost: 'text-slate-600 hover:bg-slate-100 hover:text-ink',
    danger: 'bg-rose-600 text-white hover:bg-rose-700',
  };
  const sizes = {
    sm: 'min-h-8 px-2.5 py-1 text-xs',
    md: 'min-h-10 px-4 py-2 text-sm',
    lg: 'min-h-12 px-6 py-3 text-base',
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-xl font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${sizes[size]} ${variants[variant]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

export function Input({
  label,
  hint,
  error,
  required,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string; error?: string }) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-center justify-between text-sm font-semibold text-ink">
        <span>
          {label}
          {required && <span className="ml-1 text-rose-600">*</span>}
        </span>
        <span className="text-xs font-normal text-slate-400">{hint}</span>
      </span>
      <input
        required={required}
        className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink shadow-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none ${error ? 'border-rose-400' : ''} ${className}`}
        {...props}
      />
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  );
}

export function Select({
  label,
  hint,
  error,
  required,
  children,
  className = '',
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string; error?: string }) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-center justify-between text-sm font-semibold text-ink">
        <span>
          {label}
          {required && <span className="ml-1 text-rose-600">*</span>}
        </span>
        <span className="text-xs font-normal text-slate-400">{hint}</span>
      </span>
      <select
        required={required}
        className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink shadow-sm focus:border-teal-500 focus:outline-none ${error ? 'border-rose-400' : ''} ${className}`}
        {...props}
      >
        {children}
      </select>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  );
}

export function Textarea({
  label,
  hint,
  error,
  required,
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-center justify-between text-sm font-semibold text-ink">
        <span>
          {label}
          {required && <span className="ml-1 text-rose-600">*</span>}
        </span>
        <span className="text-xs font-normal text-slate-400">{hint}</span>
      </span>
      <textarea
        required={required}
        className={`min-h-28 w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink shadow-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none ${error ? 'border-rose-400' : ''} ${className}`}
        {...props}
      />
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <div>
        {eyebrow && (
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
            {eyebrow}
          </p>
        )}
        <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{title}</h1>
        {description && (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  children,
  className = '',
  ...props
}: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={`rounded-2xl border border-white/70 bg-white p-5 shadow-soft ${className}`}
      {...props}
    >
      {children}
    </section>
  );
}

export function Loading({ label = '資料載入中…' }: { label?: string }) {
  return (
    <div className="flex min-h-40 items-center justify-center gap-3 text-sm text-slate-500">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-teal-200 border-t-teal-600" />
      {label}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="grid min-h-48 place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center">
      <div>
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-white text-2xl shadow-sm">
          ◌
        </div>
        <h3 className="font-bold text-ink">{title}</h3>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

export function Toast({
  message,
  type = 'success',
  onClose,
}: {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose?: () => void;
}) {
  const requestId = message.match(/REQ-[A-Z0-9]{8,32}/)?.[0];
  const [copied, setCopied] = useState(false);
  const tone =
    type === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : type === 'info'
        ? 'border-sky-200 bg-sky-50 text-sky-800'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800';
  return (
    <div
      className={`fixed bottom-5 right-5 z-50 flex max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold shadow-xl ${tone}`}
      role="status"
    >
      <span>{type === 'error' ? '!' : type === 'info' ? 'i' : '✓'}</span>
      <span className="flex-1">
        {message}
        {requestId && (
          <button
            type="button"
            className="mt-2 block rounded-lg border border-current/20 px-2 py-1 text-xs opacity-80 hover:opacity-100"
            onClick={() => {
              void navigator.clipboard.writeText(requestId).then(() => setCopied(true));
            }}
          >
            {copied ? '已複製錯誤編號' : '複製錯誤編號'}
          </button>
        )}
      </span>
      {onClose && (
        <button className="opacity-60 hover:opacity-100" onClick={onClose} aria-label="關閉提示">
          ×
        </button>
      )}
    </div>
  );
}

export function Table({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`overflow-x-auto rounded-2xl border border-slate-100 ${className}`}>
      <table className="w-full min-w-[720px] border-collapse text-left text-sm">{children}</table>
    </div>
  );
}

export function StatCard({
  label,
  value,
  tone = 'teal',
  note,
}: {
  label: string;
  value: string | number;
  tone?: 'teal' | 'amber' | 'violet' | 'rose';
  note?: string;
}) {
  const styles = {
    teal: 'bg-teal-50 text-teal-700',
    amber: 'bg-amber-50 text-amber-800',
    violet: 'bg-violet-50 text-violet-800',
    rose: 'bg-rose-50 text-rose-800',
  };
  return (
    <div className={`rounded-2xl p-4 ${styles[tone]}`}>
      <p className="text-xs font-bold opacity-75">{label}</p>
      <p className="mt-2 text-3xl font-extrabold">{value}</p>
      {note && <p className="mt-1 text-xs opacity-70">{note}</p>}
    </div>
  );
}

export function RatingInput({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-ink">{label}</p>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            type="button"
            key={score}
            className={`grid h-9 w-9 place-items-center rounded-xl text-lg transition ${score <= value ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-400 hover:bg-amber-100'}`}
            onClick={() => onChange(score)}
            aria-label={`${score} 分`}
            aria-pressed={score === value}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

export function Shell({
  user,
  onLogout,
  children,
}: {
  user: User;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productMasterPendingCount, setProductMasterPendingCount] = useState<number | null>(null);
  const location = useLocation();
  useEffect(() => {
    if (user.role !== 'ADMIN' && user.role !== 'BUYER') return;
    let active = true;
    api.productMasterWorkItems
      .list()
      .then((result) => {
        if (active)
          setProductMasterPendingCount(
            Number(result.summary.pending ?? 0) +
              Number(result.summary.in_progress ?? 0) +
              Number(result.summary.data_incomplete ?? 0),
          );
      })
      .catch(() => {
        if (active) setProductMasterPendingCount(null);
      });
    return () => {
      active = false;
    };
  }, [location.pathname, user.role]);
  const navGroups = [
    {
      label: '',
      items: [
        { label: '儀表板', to: '/', icon: '⌂', roles: ['ADMIN', 'BUYER', 'STORE', 'FINANCE'] },
      ],
    },
    {
      label: '廠商管理',
      items: [
        {
          label: '廠商列表／主檔',
          to: '/vendors',
          icon: '◎',
          roles: ['ADMIN', 'BUYER', 'FINANCE'],
        },
        {
          label: '待處理廠商資料變更',
          to: '/vendor-change-requests',
          icon: '↺',
          roles: ['ADMIN', 'BUYER', 'FINANCE'],
        },
        { label: '廠商申請審核', to: '/vendor-applications', icon: '✓', roles: ['ADMIN', 'BUYER'] },
      ],
    },
    {
      label: '報價活動',
      items: [
        { label: '報價活動列表', to: '/rounds', icon: '▦', roles: ['ADMIN', 'BUYER'] },
        { label: '新品提案採購審核', to: '/proactive', icon: '✦', roles: ['ADMIN', 'BUYER'] },
        {
          label:
            productMasterPendingCount === null
              ? '待建立正式商品主檔'
              : `待建立正式商品主檔 ${productMasterPendingCount}`,
          to: '/product-master-work-items',
          icon: '▤',
          roles: ['ADMIN', 'BUYER'],
        },
        {
          label: '惠生正式商品主檔',
          to: '/product-masters',
          icon: '🗂',
          roles: ['ADMIN', 'BUYER'],
        },
        { label: '新品比較', to: '/compare', icon: '⇄', roles: ['ADMIN', 'BUYER', 'STORE'] },
        { label: '會議模式', to: '/meeting', icon: '▣', roles: ['ADMIN', 'BUYER'] },
        {
          label: '新品上市公告',
          to: '/launch-announcements',
          icon: '📣',
          roles: ['ADMIN', 'BUYER', 'STORE'],
        },
        { label: '報表中心', to: '/proactive-reports', icon: '▥', roles: ['ADMIN', 'BUYER'] },
      ],
    },
    {
      label: '系統管理',
      items: [
        { label: '商品分類管理', to: '/product-categories', icon: '⌁', roles: ['ADMIN'] },
        { label: '商品子分類管理', to: '/product-subcategories', icon: '⌗', roles: ['ADMIN'] },
        { label: '系統設定', to: '/settings', icon: '⚙', roles: ['ADMIN'] },
        { label: '操作紀錄', to: '/audit', icon: '◷', roles: ['ADMIN'] },
      ],
    },
  ]
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.roles.includes(user.role)),
    }))
    .filter((group) => group.items.length > 0);
  return (
    <div className="min-h-screen bg-sand">
      <aside
        className={`no-print fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-100 bg-white px-4 py-5 transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="mb-8 flex items-center justify-between px-2">
          <Logo />
          <button
            className="rounded-lg p-2 text-slate-400 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="關閉選單"
          >
            ×
          </button>
        </div>
        <nav className="space-y-4">
          {navGroups.map((group) => (
            <div key={group.label || group.items[0].to} className="space-y-1">
              {group.label && (
                <p className="px-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-400">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${isActive ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-50 hover:text-ink'}`
                  }
                >
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-slate-50 text-sm">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="mt-8 rounded-2xl bg-ink p-4 text-white">
          <p className="text-xs text-white/60">目前登入</p>
          <p className="mt-1 font-bold">{user.display_name}</p>
          <div className="mt-2">
            <RoleBadge role={user.role} />
          </div>
          <p className="mt-3 text-xs leading-5 text-white/65">
            資料以 D1 為準，所有重要操作都會留下操作紀錄。
          </p>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-100 bg-sand/90 px-4 backdrop-blur md:px-8">
          <button
            className="rounded-xl bg-white px-3 py-2 text-slate-600 shadow-sm lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="開啟選單"
          >
            ☰
          </button>
          <Logo compact />
          <div className="flex items-center gap-3">
            <span className="hidden text-right text-xs text-slate-500 sm:block">
              {user.store_name ?? '全域資料範圍'}
              <br />
              <strong className="text-ink">{user.display_name}</strong>
            </span>
            <button
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 hover:border-rose-200 hover:text-rose-600"
              onClick={onLogout}
            >
              登出
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] px-4 py-6 md:px-8 md:py-8">{children}</main>
      </div>
      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-ink/20 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="關閉選單背景"
        />
      )}
    </div>
  );
}

export function useUnsavedPrompt(isDirty: boolean) {
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}

export function DateText({
  value,
  withTime = false,
}: {
  value: string | null | undefined;
  withTime?: boolean;
}) {
  return <span title={value ?? undefined}>{formatTaipeiDate(value, withTime)}</span>;
}

export function Money({
  value,
  currency = 'TWD',
}: {
  value: number | null | undefined;
  currency?: string;
}) {
  if (value === null || value === undefined || Number.isNaN(value)) return <span>—</span>;
  return (
    <span>
      {new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(value)}
    </span>
  );
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset?: (widgetId: string) => void;
      remove?: (widgetId: string) => void;
    };
  }
}

const isPlaceholderSiteKey = (key?: string | null) =>
  !key ||
  key.startsWith('YOUR_') ||
  key === 'YOUR_TURNSTILE_SITE_KEY_HERE' ||
  key === '1x00000000000000000000AA' ||
  key === '2x00000000000000000000AB' ||
  key === '3x00000000000000000000FF';

const isLocalHostEnvironment = () =>
  Boolean(
    import.meta.env.DEV ||
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '::1' ||
        window.location.hostname.endsWith('.localhost') ||
        window.location.hostname.endsWith('.local'))),
  );

export function TurnstileWidget({
  onToken,
  resetKey = 0,
}: {
  onToken: (token: string) => void;
  resetKey?: number;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const rawBuildSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim();
  const buildSiteKey = isPlaceholderSiteKey(rawBuildSiteKey) ? '' : rawBuildSiteKey;
  const isLocal = isLocalHostEnvironment();
  const [siteKey, setSiteKey] = useState<string | null | undefined>(
    buildSiteKey ? buildSiteKey : isLocal ? '' : undefined,
  );
  const [configError, setConfigError] = useState(false);
  useEffect(() => {
    if (buildSiteKey) {
      setSiteKey(buildSiteKey);
      setConfigError(false);
      return undefined;
    }
    if (isLocal) {
      setSiteKey('');
      setConfigError(false);
      onToken('local-dev-bypass');
      return undefined;
    }
    let cancelled = false;
    const loadRuntimeConfig = async () => {
      try {
        const response = await fetch('/api/public/vendor-proactive/config', {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('Turnstile config request failed');
        const payload = (await response.json()) as {
          data?: { turnstile_site_key?: unknown };
        };
        const rawRuntimeKey =
          typeof payload.data?.turnstile_site_key === 'string'
            ? payload.data.turnstile_site_key.trim()
            : '';
        const runtimeSiteKey = isPlaceholderSiteKey(rawRuntimeKey) ? '' : rawRuntimeKey;
        if (cancelled) return;
        if (runtimeSiteKey) {
          setSiteKey(runtimeSiteKey);
          setConfigError(false);
        } else {
          setSiteKey(isLocal ? '' : null);
          setConfigError(!isLocal);
        }
      } catch {
        if (cancelled) return;
        setSiteKey(isLocal ? '' : null);
        setConfigError(!isLocal);
      }
    };
    onToken('');
    void loadRuntimeConfig();
    return () => {
      cancelled = true;
    };
  }, [buildSiteKey, isLocal, onToken]);
  useEffect(() => {
    if (siteKey === undefined) {
      onToken('');
      return undefined;
    }
    if (!siteKey) {
      onToken(isLocal ? 'local-dev-bypass' : '');
      return undefined;
    }
    const render = () => {
      if (!elementRef.current || !window.turnstile || widgetRef.current) return;
      const clearToken = () => {
        onToken('');
        if (widgetRef.current && window.turnstile?.reset) window.turnstile.reset(widgetRef.current);
      };
      const handleError = () => {
        if (isLocal) onToken('local-dev-bypass');
        else clearToken();
      };
      widgetRef.current = window.turnstile.render(elementRef.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': clearToken,
        'error-callback': handleError,
      });
    };
    if (window.turnstile) render();
    const existing = document.querySelector('script[data-turnstile]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = 'true';
      script.addEventListener('load', render);
      document.head.appendChild(script);
    } else {
      existing.addEventListener('load', render);
    }
    return () => {
      if (widgetRef.current && window.turnstile?.remove) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [onToken, siteKey]);
  useEffect(() => {
    if (resetKey > 0 && widgetRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetRef.current);
      onToken('');
    }
  }, [onToken, resetKey]);
  return (
    <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3">
      <div ref={elementRef} />
      <p className="mt-2 text-xs text-slate-500">
        {siteKey === undefined
          ? '正在載入安全驗證…'
          : siteKey
            ? '請完成 Cloudflare Turnstile 安全驗證。'
            : configError
              ? '安全驗證服務尚未設定，請聯絡管理員。'
              : '本機開發模式：略過 Turnstile；正式環境請設定安全驗證。'}
      </p>
      <p className="hidden" aria-hidden="true">
        {siteKey
          ? '提交前需要完成 Cloudflare 安全驗證。'
          : '本機開發模式：略過 Turnstile；正式環境請設定 VITE_TURNSTILE_SITE_KEY。'}
      </p>
    </div>
  );
}
